import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MdChevronRight, MdCheckCircle } from 'react-icons/md';
import './SignUp.css';
import backgroundImage from '../../assets/images/Background.jpeg';
import logo from '../../assets/images/logo.png';
import { registerUser, resendOTP, verifyOTP, submitKYC, uploadKYCFile, sendLoginOTP, verifyLoginOTP, getAuthToken, setAuthToken, setRefreshToken, getProfile, updateProfile, uploadProfilePhoto, requestRole, getSystemAgreementState, getActiveSystemAgreement, acceptSystemAgreement } from '../../api/services/authService';
import { normalizeAgreementPayload, normalizeAgreementState } from '../../utils/systemAgreement';

const KYC_UPLOAD_ITEMS = [
  {
    id: 'idFront',
    title: 'Upload Front Side',
    description: 'Upload the front side of your government ID',
  },
  {
    id: 'idBack',
    title: 'Upload Back Side',
    description: 'Upload the back side of your government ID',
  },
  {
    id: 'selfie',
    title: 'Take Selfie',
    description: 'Take a clear selfie of yourself',
  },
];

const getNextKycUpload = (urls) => {
  if (!urls.idFront) return 'idFront';
  if (!urls.idBack) return 'idBack';
  if (!urls.selfie) return 'selfie';
  return null;
};

const KycIdFrontIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <rect x="8" y="12" width="32" height="24" rx="3" fill="#0474C4" fillOpacity="0.15" stroke="#0474C4" strokeWidth="1.5" />
    <circle cx="18" cy="22" r="4" fill="#0474C4" fillOpacity="0.4" />
    <rect x="26" y="19" width="12" height="2" rx="1" fill="#0474C4" fillOpacity="0.5" />
    <rect x="26" y="24" width="10" height="2" rx="1" fill="#0474C4" fillOpacity="0.35" />
    <rect x="26" y="29" width="8" height="2" rx="1" fill="#0474C4" fillOpacity="0.25" />
  </svg>
);

const KycIdBackIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <rect x="8" y="12" width="32" height="24" rx="3" fill="#0474C4" fillOpacity="0.15" stroke="#0474C4" strokeWidth="1.5" />
    <rect x="12" y="16" width="24" height="4" rx="1" fill="#1a1a1a" fillOpacity="0.25" />
    <rect x="12" y="28" width="20" height="2" rx="1" fill="#0474C4" fillOpacity="0.35" />
    <rect x="12" y="32" width="16" height="2" rx="1" fill="#0474C4" fillOpacity="0.25" />
  </svg>
);

const KycSelfieIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <path d="M14 16 L14 12 L18 12" stroke="#0474C4" strokeWidth="2" strokeLinecap="round" />
    <path d="M34 16 L34 12 L30 12" stroke="#0474C4" strokeWidth="2" strokeLinecap="round" />
    <path d="M14 32 L14 36 L18 36" stroke="#0474C4" strokeWidth="2" strokeLinecap="round" />
    <path d="M34 32 L34 36 L30 36" stroke="#0474C4" strokeWidth="2" strokeLinecap="round" />
    <circle cx="24" cy="24" r="8" fill="#0474C4" fillOpacity="0.2" stroke="#0474C4" strokeWidth="1.5" />
    <ellipse cx="24" cy="34" rx="10" ry="5" fill="#0474C4" fillOpacity="0.15" />
  </svg>
);

const KYC_ITEM_ICONS = {
  idFront: KycIdFrontIcon,
  idBack: KycIdBackIcon,
  selfie: KycSelfieIcon,
};

// Storage keys
const STORAGE_KEY_USERS = 'tenantscore_users_v1';
const STORAGE_KEY_KYC = 'tenantscore_kyc_v1';

// Helper functions for localStorage
const loadUsers = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_USERS);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch (_) {
    return [];
  }
};

const saveUser = (user) => {
  try {
    const users = loadUsers();
    const existingIndex = users.findIndex(u => 
      u.email.toLowerCase() === user.email.toLowerCase() || 
      u.phone === user.phone
    );
    
    if (existingIndex >= 0) {
      users[existingIndex] = { ...users[existingIndex], ...user };
    } else {
      users.push(user);
    }
    
    localStorage.setItem(STORAGE_KEY_USERS, JSON.stringify(users));
    return true;
  } catch (_) {
    return false;
  }
};


const saveKYC = (userId, kycData) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_KYC);
    const allKYC = raw ? JSON.parse(raw) : {};
    allKYC[userId] = { ...kycData, submittedDate: new Date().toISOString().split('T')[0] };
    localStorage.setItem(STORAGE_KEY_KYC, JSON.stringify(allKYC));
    return true;
  } catch (_) {
    return false;
  }
};


const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if coming from Google Sign-In
  const isFromGoogle = location.state?.fromGoogle === true;
  const googleUserData = location.state || {};
  
  // Step tracking (0 = Google profile completion, 1 = Contact, 2 = Registration, etc.)
  const [currentStep, setCurrentStep] = useState(isFromGoogle ? 0 : 1);
  const [selectedRole, setSelectedRole] = useState('');
  
  // Step 0: Google Profile Completion (for new Google users)
  // eslint-disable-next-line no-unused-vars
  const [googleProfileData, setGoogleProfileData] = useState({
    firstName: googleUserData.firstName || '',
    lastName: googleUserData.lastName || '',
    email: googleUserData.email || '',
    phone: '',
    idNumber: '',
    dob: '',
    profilePhotoFile: null,
    profilePhotoPreview: googleUserData.profilePhotoPath || null,
    registerAs: '',
    twoFaMethod: 'NONE'
  });
  // eslint-disable-next-line no-unused-vars
  const [isUpdatingGoogleProfile, setIsUpdatingGoogleProfile] = useState(false);
  
  // Step 1: Contact
  const [contactMethod, setContactMethod] = useState(''); // 'email' or 'phone'
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [existingUser, setExistingUser] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [loginToken, setLoginToken] = useState(null); // Token from send-login-otp
  const [accountFound, setAccountFound] = useState(false); // Track if account was found
  
  // Step 2: Registration data
  const [registrationData, setRegistrationData] = useState({
    firstName: '',
    lastName: '',
    idNumber: '',
    dob: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    registerAs: '',
    referredByCode: ''
  });
  
  // Loading state for API calls
  const [isLoading, setIsLoading] = useState(false);
  
  // Message state for displaying success/error messages
  const [message, setMessage] = useState({ text: '', type: '' }); // type: 'success' or 'error'
  
  // Step 3: OTP Verification
  const [otpCode, setOtpCode] = useState('');
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false); // Track if phone is verified

  // Step 4: User agreement
  const [agreementState, setAgreementState] = useState(null);
  const [agreementData, setAgreementData] = useState(null);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [agreementLoading, setAgreementLoading] = useState(false);
  const [agreementError, setAgreementError] = useState('');
  
  // Step 5: KYC Documents
  const [kycDocuments, setKycDocuments] = useState({
    idFront: null,
    idBack: null,
    selfie: null,
    idType: 'NATIONAL_ID'
  });
  
  // KYC Upload state - tracks which document to upload next and uploaded URLs
  const [kycUploadState, setKycUploadState] = useState({
    currentUpload: 'idFront', // 'idFront', 'idBack', 'selfie', or null when all done
    uploadedUrls: {
      idFront: null,
      idBack: null,
      selfie: null
    },
    isUploading: false,
    uploadingDoc: null,
  });
  
  // Loading state for KYC submission
  const [isSubmittingKYC, setIsSubmittingKYC] = useState(false);
  
  // Complete signup
  const handleComplete = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  // Handle Google Profile Photo Upload
  // eslint-disable-next-line no-unused-vars
  const handleGoogleProfilePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setMessage({ text: 'Please select an image file', type: 'error' });
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ text: 'Image size should be less than 5MB', type: 'error' });
        return;
      }
      
      setGoogleProfileData(prev => ({ ...prev, profilePhotoFile: file }));
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setGoogleProfileData(prev => ({ ...prev, profilePhotoPreview: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle Google Profile Completion Submission
  // eslint-disable-next-line no-unused-vars
  const handleGoogleProfileSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: '', type: '' });
    
    // Validation
    if (!googleProfileData.phone.trim()) {
      setMessage({ text: 'Please enter your phone number', type: 'error' });
      return;
    }
    
    if (!googleProfileData.idNumber.trim()) {
      setMessage({ text: 'Please enter your ID number', type: 'error' });
      return;
    }
    
    if (!googleProfileData.dob) {
      setMessage({ text: 'Please select your date of birth', type: 'error' });
      return;
    }
    
    if (!googleProfileData.registerAs) {
      setMessage({ text: 'Please select a role', type: 'error' });
      return;
    }
    
    setIsUpdatingGoogleProfile(true);
    
    try {
      let profilePhotoUrl = '';
      
      // Upload profile photo if provided
      if (googleProfileData.profilePhotoFile && googleUserData.userId) {
        setMessage({ text: 'Uploading profile photo...', type: 'success' });
        const uploadResponse = await uploadProfilePhoto(googleProfileData.profilePhotoFile, googleUserData.userId);
        if (uploadResponse.status === 'success' && uploadResponse.data?.url) {
          profilePhotoUrl = uploadResponse.data.url;
          console.log('Profile photo uploaded:', profilePhotoUrl);
        } else {
          console.warn('Profile photo upload failed, continuing without photo');
        }
      }
      
      // Update profile via API
      setMessage({ text: 'Completing your profile...', type: 'success' });
      
      const profileUpdateData = {
        firstName: googleProfileData.firstName,
        lastName: googleProfileData.lastName,
        phone: googleProfileData.phone.trim(),
        idNumber: googleProfileData.idNumber.trim(),
        dob: googleProfileData.dob,
        twoFaMethod: googleProfileData.twoFaMethod,
        registerAs: googleProfileData.registerAs.toUpperCase(),
        ...(profilePhotoUrl && { profilePhotoUrl })
      };
      
      console.log('Updating profile with:', profileUpdateData);
      
      const response = await updateProfile(profileUpdateData);
      
      if (response.status === 'success') {
        console.log('Profile updated successfully:', response);
        
        // Submit role request
        setMessage({ text: 'Submitting role request...', type: 'success' });
        
        try {
          const roleToRequest = googleProfileData.registerAs.toUpperCase();
          console.log('Requesting role:', roleToRequest);
          
          const roleResponse = await requestRole(roleToRequest);
          
          if (roleResponse.status === 'success') {
            console.log('Role request submitted successfully:', roleResponse);
            setMessage({ 
              text: 'Profile completed and role request submitted! OTP has been sent to your phone number. Please check your messages.', 
              type: 'success' 
            });
          } else {
            console.warn('Role request may have failed:', roleResponse);
            // Continue anyway - role might already be requested
            setMessage({ 
              text: 'Profile completed! OTP has been sent to your phone number. Please check your messages.', 
              type: 'success' 
            });
          }
        } catch (roleError) {
          console.error('Role request error:', roleError);
          // Continue with flow even if role request fails - user can request later
          setMessage({ 
            text: 'Profile completed! OTP has been sent to your phone number. Please check your messages.', 
            type: 'success' 
          });
        }
        
        // Set selected role for next steps
        setSelectedRole(googleProfileData.registerAs.toLowerCase());
        
        // Store user data for OTP step
        setExistingUser({
          id: googleUserData.userId || response.data?.id,
          email: googleProfileData.email,
          phone: googleProfileData.phone.trim(),
          firstName: googleProfileData.firstName,
          lastName: googleProfileData.lastName
        });
        
        // Set OTP resend timer (60 seconds) since OTP was just sent
        setOtpResendTimer(60);
        
        // Proceed to OTP verification step (step 3)
        setTimeout(() => {
          setCurrentStep(3);
        }, 2000);
      } else {
        setMessage({ 
          text: response.message || 'Failed to complete profile. Please try again.', 
          type: 'error' 
        });
      }
    } catch (error) {
      console.error('Profile completion error:', error);
      setMessage({ 
        text: error.message || 'Failed to complete profile. Please try again.', 
        type: 'error' 
      });
    } finally {
      setIsUpdatingGoogleProfile(false);
    }
  };
  
  // Check if email/phone exists and send login OTP
  const checkUserExists = async () => {
    setMessage({ text: '', type: '' }); // Clear previous messages
    
    if (!contactMethod) {
      setMessage({ text: 'Please select a contact method', type: 'error' });
      return;
    }
    
    if (!emailOrPhone.trim()) {
      setMessage({ 
        text: contactMethod === 'email' ? 'Please enter your email address' : 'Please enter your phone number', 
        type: 'error' 
      });
      return;
    }
    
    // Basic validation based on contact method
    if (contactMethod === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailOrPhone.trim())) {
        setMessage({ text: 'Please enter a valid email address', type: 'error' });
        return;
      }
    } else if (contactMethod === 'phone') {
      // Basic phone validation (at least 10 digits)
      const phoneRegex = /^[\d\s+()-]{10,}$/;
      if (!phoneRegex.test(emailOrPhone.trim())) {
        setMessage({ text: 'Please enter a valid phone number', type: 'error' });
        return;
      }
    }
    
    setIsLoading(true);
    
    try {
      // Format phone number if needed
      let phoneNumber = null;
      if (contactMethod === 'phone') {
        phoneNumber = emailOrPhone.trim();
        if (!phoneNumber.startsWith('+')) {
          if (phoneNumber.startsWith('0')) {
            phoneNumber = '+254' + phoneNumber.substring(1);
          } else if (phoneNumber.startsWith('254')) {
            phoneNumber = '+' + phoneNumber;
          } else {
            phoneNumber = '+254' + phoneNumber;
          }
        }
      }
      
      // Call send-login-otp endpoint
      const response = await sendLoginOTP(
        contactMethod === 'email' ? emailOrPhone.trim() : null,
        contactMethod === 'phone' ? phoneNumber : null
      );
      
      if (response.status === 'success' && response.data?.token) {
        // Account found - OTP sent
        setLoginToken(response.data.token);
        setAccountFound(true);
        setOtpResendTimer(60); // 60 seconds cooldown
        
        // Check if response includes user info to determine if we should skip OTP
        // If user info is available and KYC is complete, redirect to login
        if (response.data?.user) {
          const user = response.data.user;
          const userKycStatus = user.kycStatus || 'NONE';
          
          const isKycSubmitted = userKycStatus === 'PENDING' || userKycStatus === 'VERIFIED' || userKycStatus === 'pending' || userKycStatus === 'verified';
          
          // If KYC is submitted, redirect to login
          if (isKycSubmitted) {
            setMessage({ 
              text: 'Account already exists and setup is complete. Redirecting to login...', 
              type: 'success' 
            });
            
            setTimeout(() => {
              navigate('/login');
            }, 2000);
            return;
          }
        }
        
        // Clean the message to remove technical details
        let cleanMessage = response.message || 'OTP has been sent to your phone number. Please enter it below.';
        if (cleanMessage.includes('Use this token')) {
          // Remove everything from "Use this token" onwards
          cleanMessage = cleanMessage.split('Use this token')[0].trim();
          // Remove trailing period if it exists
          if (cleanMessage.endsWith('.')) {
            cleanMessage = cleanMessage.slice(0, -1);
          }
        }
        
        setMessage({ 
          text: cleanMessage || 'OTP has been sent to your phone number. Please enter it below.', 
          type: 'success' 
        });
      } else if (response.status === 'error' && response.message?.toLowerCase().includes('no account found')) {
        // User not found - proceed to registration
        setAccountFound(false);
        setCurrentStep(2);
      } else {
        setMessage({ text: response.message || 'Failed to send OTP. Please try again.', type: 'error' });
      }
    } catch (error) {
      const errorMessage = error.response?.message || error.message || '';
      const errorLower = errorMessage.toLowerCase();
      const isSuspended = error.status === 403 && (errorLower.includes('suspended') || errorLower.includes('suspend'));
      
      // Check if user not found OR account is suspended
      // If suspended, allow user to proceed to registration silently (don't show error)
      if (errorLower.includes('no account found') || isSuspended) {
        // User not found or account suspended - proceed to registration silently
        // Don't show error message, just proceed to registration step
        setAccountFound(false);
        setCurrentStep(2);
        // Clear any error messages
        setMessage({ text: '', type: '' });
      } else {
        // Only show error for other cases (not "no account found" or suspended)
        console.error('Send login OTP error:', error);
        setMessage({ 
          text: errorMessage || 'Failed to send OTP. Please try again.', 
          type: 'error' 
        });
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle registration
  const handleRegistration = async (e) => {
    e.preventDefault();
    setMessage({ text: '', type: '' }); // Clear previous messages
    
    // Validation
    if (!registrationData.firstName || !registrationData.lastName || !registrationData.idNumber || 
        !registrationData.dob || !registrationData.phone || !registrationData.email || 
        !registrationData.password || !registrationData.confirmPassword || !registrationData.registerAs) {
      setMessage({ text: 'Please fill in all required fields', type: 'error' });
      return;
    }
    
    if (registrationData.password !== registrationData.confirmPassword) {
      setMessage({ text: 'Passwords do not match', type: 'error' });
      return;
    }
    
    if (registrationData.password.length < 6) {
      setMessage({ text: 'Password must be at least 6 characters', type: 'error' });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Map UI role values to API role values
      const roleMap = {
        'landlord': 'LANDLORD',
        'bnb-host': 'BNB_HOST',
        'agent': 'AGENT',
        'tenant': 'TENANT',
        'guest': 'GUEST'
      };
      
      // Prepare API payload - matching the exact structure from the API
      const userData = {
        firstName: registrationData.firstName.trim(),
        lastName: registrationData.lastName.trim(),
        idNumber: registrationData.idNumber.trim(),
        dob: registrationData.dob,
        phone: registrationData.phone.trim(),
        email: registrationData.email.trim(),
        password: registrationData.password,
        source: 'APP',
      };
      
      // Add registerAs if selected
      if (registrationData.registerAs) {
        userData.registerAs = roleMap[registrationData.registerAs] || registrationData.registerAs.toUpperCase();
      }
      
      // Add referredByCode (optional)
      if (registrationData.referredByCode && registrationData.referredByCode.trim()) {
        userData.referredByCode = registrationData.referredByCode.trim();
      } else {
        userData.referredByCode = '';
      }
      
      // Call API
      const response = await registerUser(userData);
      
      console.log('API Response:', response); // Debug log
      
      if (response.status === 'success' && response.data) {
        // Store JWT token if provided during registration (required for OTP verification)
        if (response.data?.token || response.token) {
          const token = response.data?.token || response.token;
          const refreshToken = response.data?.refreshToken;
          setAuthToken(token);
          if (refreshToken) {
            setRefreshToken(refreshToken);
          }
        }
        
        // Store user data locally for reference
        const newUser = {
          id: response.data.userId || response.data.user?.id,
          firstName: registrationData.firstName,
          lastName: registrationData.lastName,
          fullName: `${registrationData.firstName} ${registrationData.lastName}`,
          idNumber: registrationData.idNumber,
          phone: registrationData.phone,
          email: registrationData.email,
          role: registrationData.registerAs || selectedRole,
          kycStatus: response.data.kycStatus || 'NONE',
          createdAt: new Date().toISOString()
        };
        
        // Update selectedRole for later use
        if (registrationData.registerAs) {
          setSelectedRole(registrationData.registerAs);
        }

        // Submit role request for non-Google signup as well.
        // This keeps login role validation and admin Role Requests in sync.
        if (registrationData.registerAs && registrationData.registerAs !== 'guest') {
          try {
            const roleToRequest = roleMap[registrationData.registerAs] || registrationData.registerAs.toUpperCase();
            await requestRole(roleToRequest);
          } catch (roleError) {
            console.warn('Role request submission failed during registration:', roleError);
          }
        }
        
        saveUser(newUser);
        setExistingUser(newUser);
        setKycStatus(response.data.kycStatus || 'NONE');
        
        // Reset OTP state for new verification
        setOtpCode('');
        setOtpResendTimer(0);
        setPhoneVerified(false);
        
        // Show success message
        setMessage({ 
          text: response.message || 'Registration successful! OTP has been sent to your phone. Please verify your phone number.', 
          type: 'success' 
        });
        
        // Go to OTP verification step after a short delay
        setTimeout(() => {
          setCurrentStep(3);
        }, 2000);
      } else {
        setMessage({ text: response.message || 'Registration failed. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.status
      });
      
      // Handle network errors separately
      if (error.isNetworkError) {
        setMessage({ text: error.message || 'Network error. Please check your connection and try again.', type: 'error' });
        return;
      }
      
      // Get error message from API response or error object
      const errorMessage = error.message || 'Registration failed. Please try again.';
      const errorLower = errorMessage.toLowerCase();
      const isSuspended = error.status === 403 && (errorLower.includes('suspended') || errorLower.includes('suspend'));
      
      // Don't show suspension errors during registration - they're not relevant
      if (isSuspended) {
        // Silently ignore suspension errors during registration
        // User is trying to register, suspension error is not relevant
        setMessage({ text: 'Registration failed. Please try again.', type: 'error' });
        return;
      }
      
      // Only show "Email already registered" if the error explicitly indicates it
      // Check for specific patterns that indicate duplicate email
      const isEmailAlreadyRegistered = 
        (errorLower.includes('email') && (
          errorLower.includes('already registered') ||
          errorLower.includes('already exists') ||
          errorLower.includes('already taken') ||
          errorLower.includes('already in use') ||
          errorLower.includes('duplicate email') ||
          (errorLower.includes('taken') && errorLower.includes('email')) ||
          (errorLower.includes('used') && errorLower.includes('email'))
        )) ||
        errorLower === 'email already registered' ||
        errorLower === 'email already exists';
      
      if (isEmailAlreadyRegistered) {
        setMessage({ text: 'Email already registered', type: 'error' });
      } else {
        // Show the actual error message from the API
        setMessage({ text: errorMessage, type: 'error' });
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const getKycDocLabel = (docId) => {
    if (docId === 'idFront') return 'ID Front';
    if (docId === 'idBack') return 'ID Back';
    return 'Selfie';
  };

  // Handle document upload — each card targets a specific document
  const handleKYCUpload = async (file, docType) => {
    if (!file) return;

    const currentDoc = docType;
    if (!currentDoc) return;

    if (!existingUser?.id) {
      setMessage({ text: 'User not found. Please start over.', type: 'error' });
      return;
    }

    if (!file.type.startsWith('image/')) {
      setMessage({ text: 'Please select an image file (JPG or PNG)', type: 'error' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ text: 'Image size should be less than 5MB', type: 'error' });
      return;
    }

    setKycUploadState(prev => ({ ...prev, isUploading: true, uploadingDoc: currentDoc }));
    setMessage({ text: '', type: '' });

    try {
      const userId = existingUser.id;
      setMessage({ text: `Uploading ${getKycDocLabel(currentDoc)}...`, type: 'success' });

      const response = await uploadKYCFile(file, userId);

      if (response.status === 'success' && response.data?.url) {
        const url = response.data.url;

        const storageKey = `kyc_${currentDoc}_${userId}`;
        localStorage.setItem(storageKey, url);

        setKycUploadState(prev => {
          const uploadedUrls = { ...prev.uploadedUrls, [currentDoc]: url };
          const nextDoc = getNextKycUpload(uploadedUrls);
          return {
            ...prev,
            uploadedUrls,
            currentUpload: nextDoc,
            isUploading: false,
            uploadingDoc: null,
          };
        });

        const nextDoc = getNextKycUpload({
          ...kycUploadState.uploadedUrls,
          [currentDoc]: url,
        });

        setMessage({
          text: `${getKycDocLabel(currentDoc)} uploaded successfully!${
            nextDoc
              ? ` Please upload ${getKycDocLabel(nextDoc)}.`
              : ' All documents uploaded. You can now submit KYC.'
          }`,
          type: 'success',
        });
      } else {
        setMessage({ text: response.message || 'Upload failed. Please try again.', type: 'error' });
        setKycUploadState(prev => ({ ...prev, isUploading: false, uploadingDoc: null }));
      }
    } catch (error) {
      console.error('KYC upload error:', error);
      setMessage({ text: error.message || 'Upload failed. Please try again.', type: 'error' });
      setKycUploadState(prev => ({ ...prev, isUploading: false, uploadingDoc: null }));
    }
  };
  
  const handleAgreementSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: '', type: '' });

    if (!agreementState?.required) {
      setMessage({ text: 'No user agreement is required right now. Continuing to the next step.', type: 'success' });
      setTimeout(() => setCurrentStep(5), 500);
      return;
    }

    if (!agreementAccepted) {
      setMessage({ text: 'Please confirm that you have read and accepted the user agreement.', type: 'error' });
      return;
    }

    if (agreementState?.acceptedAt) {
      setMessage({ text: 'Agreement already accepted. Continuing to the next step.', type: 'success' });
      setTimeout(() => setCurrentStep(5), 500);
      return;
    }

    if (!getAuthToken()) {
      setMessage({ text: 'Authentication is required. Please try again.', type: 'error' });
      return;
    }

    setAgreementLoading(true);

    try {
      const response = await acceptSystemAgreement();

      if (response.status === 'success') {
        setAgreementAccepted(true);
        setAgreementState(prev => prev ? { ...prev, acceptedAt: response.data?.acceptedAt || new Date().toISOString() } : prev);
        setMessage({ text: 'Agreement accepted. Continuing to the next step.', type: 'success' });
        setTimeout(() => setCurrentStep(5), 800);
      } else {
        setMessage({ text: response.message || 'Failed to accept the user agreement. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('Agreement acceptance error:', error);
      setMessage({ text: error.message || 'Failed to accept the user agreement. Please try again.', type: 'error' });
    } finally {
      setAgreementLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    if (currentStep === 4) {
      const loadAgreementState = async () => {
        setAgreementError('');
        setAgreementLoading(true);
        setAgreementData(null);
        setAgreementState(null);
        setAgreementAccepted(false);

        try {
          const stateResponse = await getSystemAgreementState();
          const normalizedState = normalizeAgreementState(stateResponse);

          if (!isMounted) return;
          setAgreementState(normalizedState);

          if (normalizedState.acceptedAt) {
            setAgreementAccepted(true);
          }

          if (!normalizedState.required) {
            setAgreementLoading(false);
            return;
          }

          const agreementResponse = await getActiveSystemAgreement();
          if (!isMounted) return;
          setAgreementData(normalizeAgreementPayload(agreementResponse));
        } catch (error) {
          if (!isMounted) return;
          setAgreementError(error.message || 'Unable to load the user agreement right now.');
        } finally {
          if (isMounted) setAgreementLoading(false);
        }
      };

      loadAgreementState();
    }

    return () => {
      isMounted = false;
    };
  }, [currentStep]);

  // Load uploaded URLs from localStorage on component mount or step change
  // Also restore phone verification status if token exists
  // Check if KYC is already submitted and skip to role request if needed
  useEffect(() => {
    if (currentStep === 5 && existingUser?.id) {
      // Check if KYC is already submitted - do this first
      const userKycStatus = kycStatus || existingUser?.kycStatus;
      const isKycSubmitted = userKycStatus === 'PENDING' || userKycStatus === 'VERIFIED' || userKycStatus === 'pending' || userKycStatus === 'verified';
      
      if (isKycSubmitted) {
        // KYC already submitted, complete signup
        setMessage({ 
          text: 'KYC already submitted. Redirecting to login...', 
          type: 'success' 
        });
        setTimeout(() => {
          handleComplete();
        }, 1000);
        return;
      }
      
      const userId = existingUser.id;
      const idFrontUrl = localStorage.getItem(`kyc_idFront_${userId}`);
      const idBackUrl = localStorage.getItem(`kyc_idBack_${userId}`);
      const selfieUrl = localStorage.getItem(`kyc_selfie_${userId}`);
      
      if (idFrontUrl || idBackUrl || selfieUrl) {
        const uploadedUrls = {
          idFront: idFrontUrl,
          idBack: idBackUrl,
          selfie: selfieUrl,
        };
        setKycUploadState(prev => ({
          ...prev,
          uploadedUrls,
          currentUpload: getNextKycUpload(uploadedUrls),
        }));
      }
      
      // Restore phone verification status if token exists
      const token = getAuthToken();
      if (token) {
        setPhoneVerified(true);
      }
    }
  }, [currentStep, existingUser?.id, existingUser?.kycStatus, kycStatus, handleComplete]);
  
  // Resend OTP
  const handleSendOTP = useCallback(async () => {
    // Check if authentication token exists (required for resend-otp)
    const token = getAuthToken();
    if (!token) {
      setMessage({ text: 'Authentication required. Please register again.', type: 'error' });
      return;
    }
    
    setIsLoading(true);
    setMessage({ text: '', type: '' });
    
    try {
      // Resend OTP using authenticated endpoint
      await resendOTP();
      
      // If we got here, the API call succeeded
      setOtpResendTimer(60); // 60 seconds cooldown
      setMessage({ text: 'OTP has been resent to your phone number.', type: 'success' });
    } catch (error) {
      console.error('Resend OTP error:', error);
      setMessage({ text: error.message || 'Failed to resend OTP. Please try again.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // OTP Resend Timer
  useEffect(() => {
    if (otpResendTimer > 0) {
      const timer = setTimeout(() => {
        setOtpResendTimer(otpResendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [otpResendTimer]);
  
  // Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!otpCode.trim() || otpCode.length !== 6) {
      setMessage({ text: 'Please enter a valid 6-digit OTP code', type: 'error' });
      return;
    }
    
    // Get phone number from existingUser or registrationData
    const phoneToVerify = existingUser?.phone || registrationData.phone;
    
    if (!phoneToVerify) {
      setMessage({ text: 'Phone number not found. Please register again.', type: 'error' });
      return;
    }
    
    // Check if token exists (required for OTP verification)
    const token = getAuthToken();
    if (!token) {
      setMessage({ text: 'Authentication required. Please try registering again.', type: 'error' });
      return;
    }
    
    setIsVerifyingOTP(true);
    setMessage({ text: '', type: '' });
    
    try {
      // Ensure phone number has + prefix
      let phoneNumber = phoneToVerify.trim();
      if (!phoneNumber.startsWith('+')) {
        // If it starts with 0, replace with +254
        if (phoneNumber.startsWith('0')) {
          phoneNumber = '+254' + phoneNumber.substring(1);
        } else if (phoneNumber.startsWith('254')) {
          phoneNumber = '+' + phoneNumber;
        } else {
          phoneNumber = '+254' + phoneNumber;
        }
      }
      
      // Verify the OTP using /api/phone/verify-otp endpoint
      const verifyResponse = await verifyOTP(phoneNumber, otpCode);
      
      if (verifyResponse.status === 'success' && verifyResponse.data?.valid) {
        // Store JWT token and refresh token if provided (may be updated)
        if (verifyResponse.data?.token || verifyResponse.token) {
          const newToken = verifyResponse.data?.token || verifyResponse.token;
          const refreshToken = verifyResponse.data?.refreshToken;
          setAuthToken(newToken);
          if (refreshToken) {
            setRefreshToken(refreshToken);
          }
        }
        
        // Mark phone as verified
        setPhoneVerified(true);
        
        // Check KYC status from response if available
        const userKycStatus = verifyResponse.data?.user?.kycStatus || existingUser?.kycStatus || 'NONE';
        if (userKycStatus && userKycStatus !== 'NONE') {
          setKycStatus(userKycStatus);
        }
        
        // OTP verified successfully - proceed to the agreement step before KYC
        setMessage({ 
          text: 'OTP verified successfully! Please review the user agreement before continuing.', 
          type: 'success' 
        });
        
        setTimeout(() => {
          setCurrentStep(4);
        }, 1500);
      } else {
        setMessage({ text: 'Invalid OTP code. Please try again.', type: 'error' });
        setOtpCode('');
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.status
      });
      
      // Provide more specific error messages
      let errorMessage = 'OTP verification failed. Please try again.';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.message) {
        errorMessage = error.response.message;
      } else if (error.status === 401) {
        errorMessage = 'Authentication failed. Please try registering again.';
      }
      
      setMessage({ text: errorMessage, type: 'error' });
      setOtpCode('');
    } finally {
      setIsVerifyingOTP(false);
    }
  };
  
  // Verify Login OTP
  const handleVerifyLoginOTP = async (e) => {
    e.preventDefault();
    
    if (!otpCode.trim() || otpCode.length !== 6) {
      setMessage({ text: 'Please enter a valid 6-digit OTP code', type: 'error' });
      return;
    }
    
    if (!loginToken) {
      setMessage({ text: 'Session expired. Please start over.', type: 'error' });
      return;
    }
    
    setIsVerifyingOTP(true);
    setMessage({ text: '', type: '' });
    
    try {
      // Verify the OTP using the token
      const verifyResponse = await verifyLoginOTP(loginToken, otpCode);
      
      if (verifyResponse.status === 'success') {
        // Store JWT token and refresh token if provided
        if (verifyResponse.data?.token || verifyResponse.token || verifyResponse.data?.accessToken) {
          const token = verifyResponse.data?.token || verifyResponse.token || verifyResponse.data?.accessToken;
          const refreshToken = verifyResponse.data?.refreshToken;
          setAuthToken(token);
          if (refreshToken) {
            setRefreshToken(refreshToken);
          }
        }
        
        // Mark phone as verified
        setPhoneVerified(true);
        
        // Store user info if available in response
        let userKycStatus = 'NONE';
        let userRoles = [];
        let userId = null;
        
        if (verifyResponse.data?.user) {
          const user = verifyResponse.data.user;
          userKycStatus = user.kycStatus || 'NONE';
          userRoles = user.roles || verifyResponse.data?.roles || [];
          userId = user.id || user.userId;
          setExistingUser({
            id: userId,
            email: user.email,
            phone: user.phone,
            firstName: user.firstName,
            lastName: user.lastName,
            kycStatus: userKycStatus,
            roles: userRoles
          });
          setKycStatus(userKycStatus);
        }
        
        // Fetch full profile to check KYC status
        try {
          const profileResponse = await getProfile();
          if (profileResponse.status === 'success' && profileResponse.data) {
            const profile = profileResponse.data;
            userKycStatus = profile.kycStatus || userKycStatus;
            userRoles = profile.roles || userRoles;
            userId = profile.id || userId;
            
            // Update existing user with full profile data
            setExistingUser({
              id: userId,
              email: profile.email || verifyResponse.data?.user?.email,
              phone: profile.phone || verifyResponse.data?.user?.phone,
              firstName: profile.firstName || verifyResponse.data?.user?.firstName,
              lastName: profile.lastName || verifyResponse.data?.user?.lastName,
              kycStatus: userKycStatus,
              roles: userRoles
            });
            setKycStatus(userKycStatus);
          }
        } catch (profileError) {
          console.error('Failed to fetch profile:', profileError);
          // Continue with data from verifyResponse
        }
        
        // Check if KYC is already submitted
        const isKycSubmitted = userKycStatus === 'PENDING' || userKycStatus === 'VERIFIED' || userKycStatus === 'pending' || userKycStatus === 'verified';
        
        // If KYC is submitted, redirect to login
        if (isKycSubmitted) {
          setMessage({ 
            text: 'Account setup complete! Your KYC has been submitted. Redirecting to login...', 
            type: 'success' 
          });
          
          setTimeout(() => {
            navigate('/login');
          }, 2000);
          return;
        }
        
        // OTP verified successfully - proceed to the agreement step
        setMessage({ 
          text: 'OTP verified successfully! Please review the user agreement before continuing.', 
          type: 'success' 
        });
        
        setTimeout(() => {
          setCurrentStep(4);
        }, 1500);
      } else {
        setMessage({ text: 'Invalid OTP code. Please try again.', type: 'error' });
        setOtpCode('');
      }
    } catch (error) {
      console.error('Verify login OTP error:', error);
      const errorMessage = error.message || 'OTP verification failed. Please try again.';
      setMessage({ text: errorMessage, type: 'error' });
      setOtpCode('');
    } finally {
      setIsVerifyingOTP(false);
    }
  };
  
  // Resend login OTP
  const handleResendLoginOTP = async () => {
    if (!contactMethod || !emailOrPhone.trim()) {
      setMessage({ text: 'Please enter your contact information first', type: 'error' });
      return;
    }
    
    setIsLoading(true);
    setMessage({ text: '', type: '' });
    
    try {
      let phoneNumber = null;
      if (contactMethod === 'phone') {
        phoneNumber = emailOrPhone.trim();
        if (!phoneNumber.startsWith('+')) {
          if (phoneNumber.startsWith('0')) {
            phoneNumber = '+254' + phoneNumber.substring(1);
          } else if (phoneNumber.startsWith('254')) {
            phoneNumber = '+' + phoneNumber;
          } else {
            phoneNumber = '+254' + phoneNumber;
          }
        }
      }
      
      const response = await sendLoginOTP(
        contactMethod === 'email' ? emailOrPhone.trim() : null,
        contactMethod === 'phone' ? phoneNumber : null
      );
      
      if (response.status === 'success' && response.data?.token) {
        setLoginToken(response.data.token);
        setOtpResendTimer(60);
        setMessage({ text: 'OTP has been resent to your phone number.', type: 'success' });
      } else {
        setMessage({ text: response.message || 'Failed to resend OTP. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('Resend login OTP error:', error);
      setMessage({ text: error.message || 'Failed to resend OTP. Please try again.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Submit KYC documents
  const handleKYCSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: '', type: '' });
    
    // Check if all documents are uploaded
    const { idFront, idBack, selfie } = kycUploadState.uploadedUrls;
    if (!idFront || !idBack || !selfie) {
      setMessage({ text: 'Please upload all documents (ID front, ID back, and selfie) before submitting.', type: 'error' });
      return;
    }
    
    if (!existingUser?.id) {
      setMessage({ text: 'User not found. Please start over.', type: 'error' });
      return;
    }
    
    // Check if phone is verified
    if (!phoneVerified) {
      setMessage({ text: 'Please verify your phone number first by going back to the OTP step.', type: 'error' });
      return;
    }
    
    // Check if authentication token exists (phone should be verified at this point)
    const token = getAuthToken();
    if (!token) {
      setMessage({ text: 'Authentication token not found. Please verify your phone number first by going back to the OTP step.', type: 'error' });
      return;
    }
    
    setIsSubmittingKYC(true);
    
    try {
      // Submit KYC with URLs from state (which are also in localStorage)
      setMessage({ text: 'Submitting KYC documents...', type: 'success' });
      
      const response = await submitKYC({
        idType: kycDocuments.idType,
        idDocumentFrontUrl: idFront,
        idDocumentBackUrl: idBack,
        selfieUrl: selfie,
      });
      
      if (response.status === 'success' && response.data) {
        const userId = existingUser.id;
        
        // Store KYC data locally for reference
        const kycData = {
          userId,
          kycId: response.data.id,
          idType: kycDocuments.idType,
          status: response.data.status || 'PENDING',
          submittedDate: new Date().toISOString().split('T')[0]
        };
        
        saveKYC(userId, kycData);
        setKycStatus(response.data.status || 'PENDING');
        
        // Clear uploaded URLs from localStorage after successful submission
        localStorage.removeItem(`kyc_idFront_${userId}`);
        localStorage.removeItem(`kyc_idBack_${userId}`);
        localStorage.removeItem(`kyc_selfie_${userId}`);
        
        setMessage({ 
          text: 'KYC documents submitted successfully! Your documents are being reviewed. Redirecting to login...', 
          type: 'success' 
        });
        
        // KYC is the final step - complete signup
        setTimeout(() => {
          handleComplete();
        }, 2000);
      } else {
        setMessage({ text: response.message || 'Failed to submit KYC documents. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('KYC submission error:', error);
      setMessage({ text: error.message || 'Failed to submit KYC documents. Please try again.', type: 'error' });
    } finally {
      setIsSubmittingKYC(false);
    }
  };
  
  // Reset form when step changes
  useEffect(() => {
    if (currentStep === 1) {
      setContactMethod('');
      setEmailOrPhone('');
      setExistingUser(null);
      setKycStatus(null);
      setSelectedRole('');
      setMessage({ text: '', type: '' }); // Clear messages when resetting
      setLoginToken(null);
      setAccountFound(false);
      setOtpCode('');
      setOtpResendTimer(0);
      setPhoneVerified(false);
      setRegistrationData({
        firstName: '',
        lastName: '',
        idNumber: '',
        dob: '',
        phone: '',
        email: '',
        password: '',
        confirmPassword: '',
        registerAs: '',
        referredByCode: ''
      });
      setKycDocuments({
        idFront: null,
        idBack: null,
        selfie: null,
        idType: 'NATIONAL_ID'
      });
      setKycUploadState({
        currentUpload: 'idFront',
        uploadedUrls: {
          idFront: null,
          idBack: null,
          selfie: null
        },
        isUploading: false
      });
    }
  }, [currentStep]);
  
  // Clear message when step changes
  useEffect(() => {
    setMessage({ text: '', type: '' });
  }, [currentStep]);
  
  return (
    <div className="signup-container">
      <div className="signup-background-blur" style={{ backgroundImage: `url(${backgroundImage})` }}></div>
      <div className="signup-overlay">
        <div className="signup-wrapper">
          {/* Left Section - Welcome */}
          <div className="signup-welcome-section">
            <div className="welcome-content">
              <img src={logo} alt="Tenant Score Logo" className="welcome-logo" />
              <div className="welcome-text">
                <span className="welcome-hello">Hello</span>
                <span className="welcome-main">welcome</span>
              </div>
              <div className="contact-separator"></div>
              <div className="contact-details-section">
                <h3 className="contact-title">Contact Details</h3>
                <p className="contact-address">P. O. Box 12345, Nairobi, Kenya</p>
                <div className="contact-info">
                  <span className="contact-label">Email:</span>
                  <a href="mailto:info@tenantscore.com" className="contact-value contact-link">info@tenantscore.com</a>
                </div>
                <div className="contact-info">
                  <span className="contact-label">Support:</span>
                  <a href="mailto:support@tenantscore.co.ke" className="contact-value contact-link">support@tenantscore.co.ke</a>
                </div>
                <div className="contact-info">
                  <span className="contact-label">Tel:</span>
                  <a href="tel:+254113886814" className="contact-value contact-link">+254113886814</a>
                </div>
              </div>
              <div className="progress-indicator">
                {isFromGoogle && currentStep === 0 ? (
                  <>
                    <div className="progress-step">
                      <span>Profile</span>
                    </div>
                    <div className="progress-line"></div>
                    <div className="progress-step">
                      <span>OTP</span>
                    </div>
                    <div className="progress-line"></div>
                    <div className="progress-step">
                      <span>Agreement</span>
                    </div>
                    <div className="progress-line"></div>
                    <div className="progress-step">
                      <span>KYC</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="progress-step">
                      <span>Contact</span>
                    </div>
                    <div className="progress-line"></div>
                    <div className="progress-step">
                      <span>Register</span>
                    </div>
                    <div className="progress-line"></div>
                    <div className="progress-step">
                      <span>OTP</span>
                    </div>
                    <div className="progress-line"></div>
                    <div className="progress-step">
                      <span>Agreement</span>
                    </div>
                    <div className="progress-line"></div>
                    <div className="progress-step">
                      <span>KYC</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Right Section - Sign Up Form */}
          <div className="signup-form-section">
            <form className="signup-form"             onSubmit={
              currentStep === 0 ? handleGoogleProfileSubmit :
              currentStep === 1 ? (e) => { e.preventDefault(); if (!accountFound) checkUserExists(); } :
              currentStep === 2 ? handleRegistration :
              currentStep === 3 ? handleVerifyOTP :
              currentStep === 4 ? handleAgreementSubmit :
              currentStep === 5 ? handleKYCSubmit :
              (e) => e.preventDefault()
            }>
              <h2 className="form-title">
                {currentStep === 0 ? 'Complete Your Profile' : 'Create your account'}
              </h2>

              {/* Message Display Card */}
              {message.text && (
                <div className={`alert ${
                  message.type === 'success' ? 'alert-success' : 
                  message.type === 'info' ? 'alert-info' : 
                  'alert-error'
                }`}>
                  {message.text}
                </div>
              )}

              {/* Step 0: Google Profile Completion */}
              {currentStep === 0 && (
                <div className="step-content">
                  <p className="form-subtitle" style={{ marginBottom: '24px', color: '#666' }}>
                    Please complete your profile to continue. Your name and email have been auto-filled from Google.
                  </p>

                  <div className="form-group">
                    <label htmlFor="google-firstName">First Name *</label>
                    <input
                      type="text"
                      id="google-firstName"
                      className="form-input"
                      value={googleProfileData.firstName}
                      onChange={(e) => setGoogleProfileData(prev => ({ ...prev, firstName: e.target.value }))}
                      required
                      disabled={isUpdatingGoogleProfile}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="google-lastName">Last Name *</label>
                    <input
                      type="text"
                      id="google-lastName"
                      className="form-input"
                      value={googleProfileData.lastName}
                      onChange={(e) => setGoogleProfileData(prev => ({ ...prev, lastName: e.target.value }))}
                      required
                      disabled={isUpdatingGoogleProfile}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="google-email">Email Address</label>
                    <input
                      type="email"
                      id="google-email"
                      className="form-input"
                      value={googleProfileData.email}
                      disabled
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Email is set from your Google account and cannot be changed
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="google-phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="google-phone"
                      className="form-input"
                      placeholder="Enter your phone number (e.g., +254700000000)"
                      value={googleProfileData.phone}
                      onChange={(e) => setGoogleProfileData(prev => ({ ...prev, phone: e.target.value }))}
                      required
                      disabled={isUpdatingGoogleProfile}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="google-idNumber">ID Number *</label>
                    <input
                      type="text"
                      id="google-idNumber"
                      className="form-input"
                      placeholder="Enter your ID number"
                      value={googleProfileData.idNumber}
                      onChange={(e) => setGoogleProfileData(prev => ({ ...prev, idNumber: e.target.value }))}
                      required
                      disabled={isUpdatingGoogleProfile}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="google-dob">Date of Birth *</label>
                    <input
                      type="date"
                      id="google-dob"
                      className="form-input"
                      value={googleProfileData.dob}
                      onChange={(e) => setGoogleProfileData(prev => ({ ...prev, dob: e.target.value }))}
                      required
                      disabled={isUpdatingGoogleProfile}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="google-registerAs">I want to register as *</label>
                    <select
                      id="google-registerAs"
                      className="form-input form-select"
                      value={googleProfileData.registerAs}
                      onChange={(e) => setGoogleProfileData(prev => ({ ...prev, registerAs: e.target.value }))}
                      required
                      disabled={isUpdatingGoogleProfile}
                    >
                      <option value="">Select a role</option>
                      <option value="landlord">Landlord</option>
                      <option value="agent">Agent</option>
                      <option value="bnb-host">BNB Host</option>
                      <option value="tenant">Tenant</option>
                      <option value="guest">Guest</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="google-profilePhoto">Profile Photo (Optional)</label>
                    <input
                      type="file"
                      id="google-profilePhoto"
                      className="form-input"
                      accept="image/*"
                      onChange={handleGoogleProfilePhotoChange}
                      disabled={isUpdatingGoogleProfile}
                    />
                    {googleProfileData.profilePhotoPreview && (
                      <div style={{ marginTop: '12px' }}>
                        <img 
                          src={googleProfileData.profilePhotoPreview} 
                          alt="Profile preview" 
                          style={{ 
                            width: '100px', 
                            height: '100px', 
                            objectFit: 'cover', 
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb'
                          }} 
                        />
                      </div>
                    )}
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Upload a profile photo (max 5MB, JPG/PNG)
                    </small>
                  </div>

                  <div className="form-buttons" style={{ marginTop: '24px' }}>
                    <button 
                      type="submit" 
                      className="btn-login" 
                      disabled={isUpdatingGoogleProfile}
                    >
                      {isUpdatingGoogleProfile ? 'Completing Profile...' : 'Complete Profile'}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 1: Email/Phone Check */}
              {currentStep === 1 && (
                <div className="step-content">
                  <div className="form-group">
                    <label htmlFor="contactMethod">Contact Method *</label>
                    <select
                      id="contactMethod"
                      className="form-input form-select"
                      value={contactMethod}
                      onChange={(e) => {
                        setContactMethod(e.target.value);
                        setEmailOrPhone(''); // Clear input when method changes
                        setAccountFound(false); // Reset account found state
                        setLoginToken(null); // Clear token
                        setOtpCode(''); // Clear OTP
                      }}
                      required
                      disabled={accountFound}
                    >
                      <option value="">Select contact method</option>
                      <option value="email">Email Address</option>
                      <option value="phone">Phone Number</option>
                    </select>
                    <small className="form-hint">Choose how you want to be contacted</small>
                  </div>

                  {contactMethod && (
                    <div className="form-group">
                      <label htmlFor="emailOrPhone">
                        {contactMethod === 'email' ? 'Email Address *' : 'Phone Number *'}
                      </label>
                      <input
                        type={contactMethod === 'email' ? 'email' : 'tel'}
                        id="emailOrPhone"
                        className="form-input"
                        placeholder={
                          contactMethod === 'email' 
                            ? 'Enter your email address' 
                            : 'Enter your phone number (e.g., +254700000000)'
                        }
                        value={emailOrPhone}
                        onChange={(e) => setEmailOrPhone(e.target.value)}
                        required
                        disabled={accountFound}
                      />
                      <small className="form-hint">
                        {contactMethod === 'email' 
                          ? "We'll check if you already have an account with this email"
                          : "We'll check if you already have an account with this phone number"}
                      </small>
                    </div>
                  )}

                  {/* OTP Input - Show when account is found */}
                  {accountFound && (
                    <>
                      <div className="form-group">
                        <label htmlFor="otp">Enter OTP Code *</label>
                        <input
                          type="text"
                          id="otp"
                          className="form-input"
                          placeholder="Enter 6-digit OTP"
                          value={otpCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setOtpCode(value);
                          }}
                          maxLength="6"
                          required
                          disabled={isVerifyingOTP}
                        />
                        <small className="form-hint">
                          We've sent a 6-digit OTP code to the phone number associated with this account
                        </small>
                      </div>

                      <div className="form-group">
                        <button
                          type="button"
                          className="link-button"
                          onClick={handleResendLoginOTP}
                          disabled={otpResendTimer > 0 || isLoading}
                          style={{ 
                            textAlign: 'center', 
                            width: '100%',
                            marginTop: '10px',
                            opacity: otpResendTimer > 0 ? 0.6 : 1,
                            cursor: otpResendTimer > 0 ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {otpResendTimer > 0 
                            ? `Resend OTP in ${otpResendTimer}s` 
                            : 'Resend OTP'}
                        </button>
                      </div>
                    </>
                  )}

                  <div className="form-buttons">
                    {!accountFound ? (
                      <button 
                        type="submit" 
                        className="btn-primary" 
                        disabled={!contactMethod || !emailOrPhone.trim() || isLoading}
                      >
                        {isLoading ? 'Checking...' : 'Continue'}
                      </button>
                    ) : (
                      <button 
                        type="button" 
                        className="btn-primary" 
                        onClick={handleVerifyLoginOTP}
                        disabled={isVerifyingOTP || otpCode.length !== 6}
                      >
                        {isVerifyingOTP ? 'Verifying...' : 'Verify OTP'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Registration (New Users Only) */}
              {currentStep === 2 && (
                <div className="step-content">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      className="form-input"
                      placeholder="Enter your first name"
                      value={registrationData.firstName}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, firstName: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      className="form-input"
                      placeholder="Enter your last name"
                      value={registrationData.lastName}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, lastName: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="idNumber">ID Number *</label>
                    <input
                      type="text"
                      id="idNumber"
                      className="form-input"
                      placeholder="Enter your ID number"
                      value={registrationData.idNumber}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, idNumber: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="dob">Date of Birth *</label>
                    <input
                      type="date"
                      id="dob"
                      className="form-input"
                      value={registrationData.dob}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, dob: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      className="form-input"
                      placeholder="Enter your phone number (e.g., +254700000000)"
                      value={registrationData.phone}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, phone: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      className="form-input"
                      placeholder="Enter your email"
                      value={registrationData.email}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, email: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">Password *</label>
                    <input
                      type="password"
                      id="password"
                      className="form-input"
                      placeholder="Enter your password"
                      value={registrationData.password}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, password: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password *</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      className="form-input"
                      placeholder="Confirm your password"
                      value={registrationData.confirmPassword}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="registerAs">Register As *</label>
                    <select
                      id="registerAs"
                      className="form-input form-select"
                      value={registrationData.registerAs}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, registerAs: e.target.value }))}
                      required
                    >
                      <option value="">Select a role</option>
                      <option value="landlord">Landlord</option>
                      <option value="bnb-host">BNB Host</option>
                      <option value="agent">Agent</option>
                      <option value="tenant">Tenant</option>
                      <option value="guest">Guest</option>
                    </select>
                    <small className="form-hint">
                      Select the role you want to register as. Admin approval may be required.
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="referredByCode">Referral Code (Optional)</label>
                    <input
                      type="text"
                      id="referredByCode"
                      className="form-input"
                      placeholder="Enter referral code if you have one"
                      value={registrationData.referredByCode}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, referredByCode: e.target.value }))}
                    />
                    <small className="form-hint">
                      If someone referred you, enter their referral code here.
                    </small>
                  </div>

                  <div className="form-buttons">
                    <button type="button" className="btn-back" onClick={() => setCurrentStep(1)} disabled={isLoading}>
                      Back
                    </button>
                    <button type="submit" className="btn-primary" disabled={isLoading}>
                      {isLoading ? 'Registering...' : 'Register'}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: OTP Verification */}
              {currentStep === 3 && (
                <div className="step-content">
                  <div className="form-group">
                    <label htmlFor="otp">Enter OTP Code *</label>
                    <input
                      type="text"
                      id="otp"
                      className="form-input"
                      placeholder="Enter 6-digit OTP"
                      value={otpCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setOtpCode(value);
                      }}
                      maxLength="6"
                      required
                      disabled={isVerifyingOTP}
                    />
                    <small className="form-hint">
                      We've sent a 6-digit OTP code to {existingUser?.phone || 'your phone number'}
                    </small>
                  </div>

                  <div className="form-group">
                    <button
                      type="button"
                      className="link-button"
                      onClick={handleSendOTP}
                      disabled={otpResendTimer > 0 || isLoading}
                      style={{ 
                        textAlign: 'center', 
                        width: '100%',
                        marginTop: '10px',
                        opacity: otpResendTimer > 0 ? 0.6 : 1,
                        cursor: otpResendTimer > 0 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {otpResendTimer > 0 
                        ? `Resend OTP in ${otpResendTimer}s` 
                        : 'Resend OTP'}
                    </button>
                  </div>

                  <div className="form-buttons">
                    <button type="button" className="btn-back" onClick={() => setCurrentStep(2)} disabled={isVerifyingOTP}>
                      Back
                    </button>
                    <button type="submit" className="btn-primary" disabled={isVerifyingOTP || otpCode.length !== 6}>
                      {isVerifyingOTP ? 'Verifying...' : 'Verify OTP'}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: User Agreement */}
              {currentStep === 4 && (
                <div className="step-content">
                  <div className="form-group">
                    <label className="kyc-upload-section-label">Read the user agreement</label>
                    <p className="form-subtitle" style={{ marginBottom: '12px' }}>
                      Please review the latest platform agreement before continuing with your registration.
                    </p>

                    {agreementLoading ? (
                      <p className="form-hint">Loading agreement...</p>
                    ) : agreementError ? (
                      <div className="alert alert-error">{agreementError}</div>
                    ) : (
                      <>
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px', background: '#f8fafc' }}>
                          <h3 style={{ margin: '0 0 8px', fontSize: '18px', color: '#02236A' }}>
                            {agreementData?.title || agreementState?.title || 'User agreement'}
                          </h3>
                          <p style={{ margin: '0 0 8px', color: '#4b5563', lineHeight: 1.6 }}>
                            {agreementData?.summary || agreementState?.summary || 'Please read and accept the platform terms before continuing.'}
                          </p>
                          {(agreementData?.agreementUrl || agreementState?.agreementUrl) && (
                            <a
                              href={agreementData?.agreementUrl || agreementState?.agreementUrl}
                              target="_blank"
                              rel="noreferrer"
                              style={{ display: 'inline-flex', marginTop: '6px', color: '#0474C4', fontWeight: 600 }}
                            >
                              Open agreement document
                            </a>
                          )}
                          {agreementData?.version && (
                            <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: '13px' }}>
                              Version {agreementData.version}
                            </p>
                          )}
                        </div>

                        {agreementState?.required !== false && (
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px', fontWeight: 600, color: '#111827' }}>
                            <input
                              type="checkbox"
                              checked={agreementAccepted}
                              onChange={(e) => setAgreementAccepted(e.target.checked)}
                            />
                            I have read and accepted the user agreement.
                          </label>
                        )}
                      </>
                    )}
                  </div>

                  <div className="form-buttons">
                    <button type="button" className="btn-back" onClick={() => setCurrentStep(3)} disabled={agreementLoading}>
                      Back
                    </button>
                    <button type="submit" className="btn-primary" disabled={agreementLoading || (agreementState?.required !== false && !agreementAccepted)}>
                      {agreementLoading ? 'Please wait...' : 'Continue'}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 5: KYC Documents */}
              {currentStep === 5 && (
                <div className="step-content">
                  {kycStatus === 'pending' && (
                    <div className="alert alert-info">
                      Your KYC documents are being verified. Please wait...
                    </div>
                  )}
                  
                  {kycStatus === 'verified' && (
                    <div className="alert alert-success">
                      Your KYC has been verified! Proceeding to next step...
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="idType">Document Type *</label>
                    <select
                      id="idType"
                      className="form-input form-select"
                      value={kycDocuments.idType}
                      onChange={(e) => setKycDocuments(prev => ({ ...prev, idType: e.target.value }))}
                      required
                      disabled={
                        isSubmittingKYC ||
                        (kycUploadState.uploadedUrls.idFront &&
                          kycUploadState.uploadedUrls.idBack &&
                          kycUploadState.uploadedUrls.selfie)
                      }
                    >
                      <option value="NATIONAL_ID">National ID</option>
                      <option value="PASSPORT">Passport</option>
                      <option value="DRIVING_LICENSE">Driving License</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="kyc-upload-section-label">Identity verification *</label>
                    <p className="kyc-upload-section-hint">Upload all three items below to complete verification.</p>
                    <div className="kyc-upload-list">
                      {KYC_UPLOAD_ITEMS.map((item) => {
                        const Icon = KYC_ITEM_ICONS[item.id];
                        const isUploaded = Boolean(kycUploadState.uploadedUrls[item.id]);
                        const isUploadingThis = kycUploadState.uploadingDoc === item.id;
                        const inputId = `kyc-upload-${item.id}`;

                        return (
                          <label
                            key={item.id}
                            htmlFor={inputId}
                            className={`kyc-upload-card${isUploaded ? ' kyc-upload-card--done' : ''}${isUploadingThis ? ' kyc-upload-card--uploading' : ''}${kycUploadState.isUploading && !isUploadingThis ? ' kyc-upload-card--disabled' : ''}`}
                          >
                            <div className="kyc-upload-card__icon">
                              <Icon />
                            </div>
                            <div className="kyc-upload-card__content">
                              <h3 className="kyc-upload-card__title">{item.title}</h3>
                              <p className="kyc-upload-card__description">{item.description}</p>
                              <p className="kyc-upload-card__requirements">
                                <MdCheckCircle className="kyc-upload-card__req-icon" aria-hidden="true" />
                                JPG, PNG • Max 5MB
                              </p>
                              <p
                                className={`kyc-upload-card__pick-status${isUploaded ? ' kyc-upload-card__pick-status--done' : ''}${isUploadingThis ? ' kyc-upload-card__pick-status--busy' : ''}`}
                              >
                                {isUploadingThis
                                  ? 'Uploading…'
                                  : isUploaded
                                    ? 'File uploaded'
                                    : 'No file selected — tap this row to choose'}
                              </p>
                            </div>
                            <input
                              type="file"
                              id={inputId}
                              className="kyc-upload-card__input"
                              accept="image/jpeg,image/png,image/jpg"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  handleKYCUpload(file, item.id);
                                }
                                e.target.value = '';
                              }}
                              disabled={kycUploadState.isUploading || isSubmittingKYC}
                            />
                            <span className="kyc-upload-card__action" aria-hidden="true">
                              {isUploadingThis ? (
                                <span className="kyc-upload-card__spinner" />
                              ) : isUploaded ? (
                                <MdCheckCircle className="kyc-upload-card__check" />
                              ) : (
                                <MdChevronRight className="kyc-upload-card__chevron" />
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="form-buttons">
                    <button type="button" className="btn-back" onClick={() => {
                      setCurrentStep(4);
                    }}>
                      Back
                    </button>
                    <button 
                      type="submit" 
                      className="btn-primary" 
                      disabled={
                        isSubmittingKYC || 
                        kycStatus === 'pending' || 
                        kycUploadState.currentUpload !== null ||
                        !kycUploadState.uploadedUrls.idFront ||
                        !kycUploadState.uploadedUrls.idBack ||
                        !kycUploadState.uploadedUrls.selfie
                      }
                    >
                      {isSubmittingKYC ? 'Submitting...' : kycStatus === 'pending' ? 'Verifying...' : 'Submit KYC'}
                    </button>
                  </div>
                </div>
              )}

              <div className="form-footer">
                <p>Already have an account? <button type="button" className="link-button" onClick={() => navigate('/login')}>Sign In</button></p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
